import React, { useState, useEffect } from 'react';
import { UserRole, PresenceStatus, AssigneeVisibility, Workspace } from '../types';
import { Button, Input, Switch, TextArea, PresenceBadge } from './UIComponents';
import {
  User,
  Building,
  Bell,
  Activity,
  Eye,
  Shield,
  CreditCard,
  AlertTriangle,
  Clock,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotificationPreferences,
  saveNotificationPreferences,
  listNotifications,
  NotificationRecord
} from '../services/notificationService';
import {
  changePassword,
  logoutOtherSessions,
  fetchSessions,
  UserSessionRecord,
  revokeSession
} from '../services/securityService';
import { deleteWorkspace } from '../services/dataService';

interface SettingsViewProps {
  role: UserRole;
  user: { name: string; email: string; initials: string; avatarColor: string };
  workspace?: Workspace | null;
  onWorkspaceDeleted?: () => void;
  allWorkspaces?: Workspace[];
}

type SectionId = 'account' | 'workspace' | 'notifications' | 'presence' | 'visibility' | 'security' | 'billing' | 'advanced';

export const SettingsView: React.FC<SettingsViewProps> = ({ role, user, workspace, onWorkspaceDeleted, allWorkspaces = [] }) => {
  const { user: authUser } = useAuth();
  const [activeSection, setActiveSection] = useState<SectionId>('account');
  const isProvider = role === UserRole.PROVIDER;

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [workspaceName, setWorkspaceName] = useState(workspace?.name || 'My Workspace');
  const [welcomeMsg, setWelcomeMsg] = useState('');

  const [prefs, setPrefs] = useState({ comments: true, approvals: true, files: false });
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);

  const [autoAway, setAutoAway] = useState(true);

  const [visStatus, setVisStatus] = useState(true);
  const [visDates, setVisDates] = useState(true);
  const [visAssignee, setVisAssignee] = useState<AssigneeVisibility>('HIDDEN');
  const [visFiles, setVisFiles] = useState(true);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isLoggingOutOthers, setIsLoggingOutOthers] = useState(false);
  const [sessions, setSessions] = useState<UserSessionRecord[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);

  const [notice, setNotice] = useState<string | null>(null);

  const navigation = [
    { id: 'account', label: 'Account', icon: User, allowed: true },
    { id: 'workspace', label: 'Workspace', icon: Building, allowed: isProvider },
    { id: 'notifications', label: 'Notifications', icon: Bell, allowed: true },
    { id: 'presence', label: 'Presence', icon: Activity, allowed: true },
    { id: 'visibility', label: 'Receiver Visibility', icon: Eye, allowed: isProvider },
    { id: 'security', label: 'Security', icon: Shield, allowed: true },
    { id: 'billing', label: 'Billing', icon: CreditCard, allowed: isProvider },
    { id: 'advanced', label: 'Advanced', icon: AlertTriangle, allowed: isProvider },
  ].filter(item => item.allowed) as { id: SectionId; label: string; icon: any }[];

  useEffect(() => {
    const load = async () => {
      if (!authUser?.id) return;
      const [loadedPrefs, loadedNotifications, loadedSessions] = await Promise.all([
        getNotificationPreferences(authUser.id),
        listNotifications(authUser.id),
        fetchSessions(authUser.id)
      ]);
      setPrefs(loadedPrefs);
      setNotifications(loadedNotifications);
      setSessions(loadedSessions);
    };
    load();
  }, [authUser?.id]);

  const handleSavePrefs = async () => {
    if (!authUser?.id) return;
    setIsSavingPrefs(true);
    try {
      await saveNotificationPreferences(authUser.id, prefs);
      setNotice('Notification preferences saved.');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleChangePassword = async () => {
    if (!password || password !== passwordConfirm) {
      setNotice('Passwords must match.');
      return;
    }
    setIsSavingPassword(true);
    try {
      await changePassword(password);
      setNotice('Password updated.');
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      console.error(err);
      setNotice('Could not update password.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleLogoutOthers = async () => {
    setIsLoggingOutOthers(true);
    try {
      await logoutOtherSessions();
      setNotice('Other sessions logged out.');
      if (authUser?.id) {
        const refreshed = await fetchSessions(authUser.id);
        setSessions(refreshed);
      }
    } finally {
      setIsLoggingOutOthers(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    await revokeSession(sessionId);
    if (authUser?.id) {
      const refreshed = await fetchSessions(authUser.id);
      setSessions(refreshed);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspace?.id || deleteConfirmation !== 'delete') return;

    // Prevent deleting the last workspace (would lock user out)
    if (allWorkspaces.length <= 1) {
      setNotice('Cannot delete your last workspace. Please create another workspace first.');
      setDeleteConfirmation('');
      return;
    }

    setIsDeletingWorkspace(true);
    try {
      await deleteWorkspace(workspace.id);
      setNotice('Workspace deleted.');
      onWorkspaceDeleted?.();
    } catch (err) {
      console.error(err);
      setNotice('Failed to delete workspace.');
    } finally {
      setIsDeletingWorkspace(false);
      setDeleteConfirmation('');
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'account':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Account</h2>
              <p className="text-[13px] text-zinc-500">Manage your personal details and preferences.</p>
            </div>

            <div className="flex items-center gap-6">
              <div className={`w-20 h-20 rounded-full ${user.avatarColor} flex items-center justify-center text-xl text-white font-medium border-4 border-zinc-950 shadow-xl`}>
                {user.initials}
              </div>
              <Button variant="secondary" size="sm">Change Photo</Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Full Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Email Address</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Timezone</label>
                <div className="relative">
                  <select className="w-full h-10 px-3 bg-zinc-950 border border-zinc-800 text-[13px] text-zinc-100 rounded-md focus:outline-none appearance-none">
                    <option>Pacific Time (US & Canada)</option>
                    <option>UTC</option>
                    <option>Eastern Time (US & Canada)</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3 text-zinc-500 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Language</label>
                <div className="relative">
                  <select className="w-full h-10 px-3 bg-zinc-950 border border-zinc-800 text-[13px] text-zinc-100 rounded-md focus:outline-none appearance-none">
                    <option>English</option>
                    <option>Spanish</option>
                    <option>French</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3 text-zinc-500 pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-zinc-900">
              <Button>Save Changes</Button>
            </div>
          </div>
        );

      case 'workspace':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Workspace</h2>
              <p className="text-[13px] text-zinc-500">Manage how your provider workspace appears to receivers.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Workspace Name</label>
                <Input value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Default Welcome Message</label>
                <TextArea className="h-24" value={welcomeMsg} onChange={(e) => setWelcomeMsg(e.target.value)} />
                <p className="text-[11px] text-zinc-600">This will be pre-filled for new projects.</p>
              </div>
            </div>
            <div className="pt-4 border-t border-zinc-900">
              <Button>Save Workspace</Button>
            </div>

            {/* Danger Zone - shortcut to delete workspace */}
            <div className="pt-8 mt-8 border-t border-red-900/20">
              <div className="mb-3">
                <h3 className="text-sm font-medium text-red-400 mb-1">Danger Zone</h3>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Permanently delete this workspace and all its projects. This action cannot be undone.
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setActiveSection('advanced')}
                disabled={allWorkspaces.length <= 1}
              >
                {allWorkspaces.length <= 1 ? 'Cannot Delete Last Workspace' : 'Delete Workspace'}
              </Button>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Notifications</h2>
              <p className="text-[13px] text-zinc-500">Control when Verline interrupts you and how we contact you.</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between py-3 border-b border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">Comments & Feedback</div>
                  <div className="text-[11px] text-zinc-500">New comments or change requests</div>
                </div>
                <Switch checked={prefs.comments} onChange={(v) => setPrefs(p => ({ ...p, comments: v }))} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">Milestone Approvals</div>
                  <div className="text-[11px] text-zinc-500">When a milestone is approved</div>
                </div>
                <Switch checked={prefs.approvals} onChange={(v) => setPrefs(p => ({ ...p, approvals: v }))} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">File Uploads</div>
                  <div className="text-[11px] text-zinc-500">When new files are added</div>
                </div>
                <Switch checked={prefs.files} onChange={(v) => setPrefs(p => ({ ...p, files: v }))} />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSavePrefs} isLoading={isSavingPrefs}>Save Preferences</Button>
            </div>

            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[13px] font-medium text-zinc-200">Recent notifications</div>
                  <div className="text-[11px] text-zinc-500">In-app alerts for this account</div>
                </div>
                <Clock size={16} className="text-zinc-500" />
              </div>
              {notifications.length === 0 ? (
                <p className="text-[12px] text-zinc-500">No notifications yet.</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {notifications.slice(0, 5).map(n => {
                    const date = n.created_at ? new Date(n.created_at) : null;
                    const dateStr = date && !isNaN(date.getTime())
                      ? date.toLocaleString()
                      : 'Recently';
                    return (
                      <div key={n.id} className="text-[12px] text-zinc-300 border border-zinc-800 rounded-md px-3 py-2">
                        <div className="text-[11px] text-zinc-500 mb-1">{dateStr}</div>
                        <div>{n.message}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'presence':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Presence & Availability</h2>
              <p className="text-[13px] text-zinc-500">Manage your status visibility.</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Default Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {[PresenceStatus.ACTIVE, PresenceStatus.AWAY, PresenceStatus.FOCUSED, PresenceStatus.OFFLINE].map(s => (
                    <button
                      key={s}
                      onClick={() => { }}
                      className="flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-800 hover:bg-zinc-900 transition-colors"
                    >
                      <PresenceBadge status={s} showLabel={false} />
                      <span className="text-[13px] text-zinc-300 capitalize">{s.toLowerCase()}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">Auto-away</div>
                  <div className="text-[11px] text-zinc-500">Set status to Away after 15 minutes of inactivity</div>
                </div>
                <Switch checked={autoAway} onChange={setAutoAway} />
              </div>
            </div>
          </div>
        );

      case 'visibility':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Receiver Visibility Defaults</h2>
              <p className="text-[13px] text-zinc-500">Set the default transparency for new projects.</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between py-3 border-b border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">Milestone Status</div>
                  <div className="text-[11px] text-zinc-500">Receivers can see current status of milestones</div>
                </div>
                <Switch checked={visStatus} onChange={setVisStatus} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">Milestone Dates</div>
                  <div className="text-[11px] text-zinc-500">Receivers can see due dates and ETAs</div>
                </div>
                <Switch checked={visDates} onChange={setVisDates} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-zinc-900">
                <div>
                  <div className="text-[13px] text-zinc-200 font-medium">Project Files</div>
                  <div className="text-[11px] text-zinc-500">Receivers can view uploaded files by default</div>
                </div>
                <Switch checked={visFiles} onChange={setVisFiles} />
              </div>
            </div>

            <div className="space-y-1.5 mt-4">
              <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Assignee Visibility</label>
              <select
                value={visAssignee}
                onChange={(e) => setVisAssignee(e.target.value as any)}
                className="w-full h-10 px-3 bg-zinc-950 border border-zinc-800 text-[13px] text-zinc-100 rounded-md focus:outline-none"
              >
                <option value="HIDDEN">Hidden (Recommended)</option>
                <option value="LEAD">Lead Only</option>
                <option value="MILESTONE">Milestone Owner</option>
              </select>
              <p className="text-[11px] text-zinc-600">Controls which team members are visible to receivers.</p>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Security</h2>
              <p className="text-[13px] text-zinc-500">Protect your account and sessions.</p>
            </div>

            <div className="space-y-3 border border-zinc-900 rounded-lg p-4">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">New password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Confirm password</label>
                <Input type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleChangePassword} isLoading={isSavingPassword} disabled={!password || !passwordConfirm}>
                  Change Password
                </Button>
              </div>
            </div>

            <div className="space-y-3 border border-zinc-900 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-medium text-zinc-200">Active sessions</div>
                  <div className="text-[11px] text-zinc-500">Devices currently signed in</div>
                </div>
                <Button size="sm" variant="ghost" onClick={handleLogoutOthers} isLoading={isLoggingOutOthers}>
                  <LogOut size={14} className="mr-2" /> Log out others
                </Button>
              </div>
              {sessions.length === 0 ? (
                <p className="text-[12px] text-zinc-500">Only this session is active.</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map(s => (
                    <div key={s.id} className="flex items-center justify-between text-[12px] text-zinc-300 border border-zinc-800 rounded-md px-3 py-2">
                      <div>
                        <div className="font-medium">{s.user_agent || 'Browser session'}</div>
                        <div className="text-[11px] text-zinc-500">Last active {s.last_active ? new Date(s.last_active).toLocaleString() : 'just now'}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleRevokeSession(s.id)}>Revoke</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Billing</h2>
              <p className="text-[13px] text-zinc-500">We’re early — your feedback helps shape the product.</p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 space-y-4">
              <p className="text-[13px] text-zinc-300">
                No plans or pricing yet. Tell us what you need and we’ll prioritise the roadmap.
              </p>
              <div className="flex gap-3">
                <Button className="flex-1">Give feedback</Button>
                <Button variant="secondary" className="flex-1">Tip / Support the product</Button>
              </div>
            </div>
          </div>
        );

      case 'advanced':
        return (
          <div className="space-y-8 max-w-lg animate-fade-in">
            <div>
              <h2 className="text-xl font-medium text-zinc-100 mb-1">Advanced</h2>
              <p className="text-[13px] text-zinc-500">Dangerous actions.</p>
            </div>

            <div className="border border-red-900/30 bg-red-950/10 rounded-lg p-5">
              <h3 className="text-[14px] font-medium text-red-400 mb-2">Delete Workspace</h3>
              <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
                This will permanently delete your workspace, all projects, and receiver data. This action cannot be undone.
                {allWorkspaces.length <= 1 && (
                  <span className="block mt-2 text-red-400 font-medium">
                    You cannot delete your last remaining workspace.
                  </span>
                )}
              </p>
              <div className="space-y-2">
                <Input
                  placeholder="Type delete to confirm"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value.toLowerCase())}
                  disabled={allWorkspaces.length <= 1}
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleteConfirmation !== 'delete' || isDeletingWorkspace || allWorkspaces.length <= 1}
                  isLoading={isDeletingWorkspace}
                  onClick={handleDeleteWorkspace}
                >
                  Delete Workspace
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-black animate-fade-in">
      {/* Sidebar */}
      <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-zinc-900 pt-6 pb-2 md:pt-8 md:pb-4 flex flex-col shrink-0 bg-black overflow-x-auto md:overflow-visible">
        <div className="px-4 md:px-6 mb-4 md:mb-6">
          <h1 className="text-2xl font-medium text-zinc-100 tracking-tight">Settings</h1>
        </div>
        <nav className="flex md:flex-col gap-1 px-3 no-scrollbar overflow-x-auto md:overflow-visible pb-2 md:pb-0">
          {navigation.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`flex-none md:w-full flex items-center gap-2 md:gap-3 px-3 py-2 rounded-md text-[13px] transition-colors whitespace-nowrap ${activeSection === item.id
                ? 'bg-zinc-900 text-zinc-100 font-medium'
                : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900/50'
                }`}
            >
              <item.icon size={16} strokeWidth={1.5} />
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-black">
        <div className="max-w-3xl px-6 md:px-12 py-8 md:py-10">
          {notice && (
            <div className="mb-4 text-[12px] text-emerald-300 bg-emerald-900/20 border border-emerald-800 rounded px-3 py-2">
              {notice}
            </div>
          )}
          {renderSection()}
        </div>
      </div>
    </div>
  );
};